import type { Block as BlockSpec } from '../spec/types'

import { Actions } from './Actions'
import { Apr } from './Apr'
import { Bal } from './Bal'
import { BlockShell } from './BlockShell'
import { Cards } from './Cards'
import { Chart } from './Chart'
import { Cmp } from './Cmp'
import { Composer } from './Composer'
import { Decide } from './Decide'
import { Form } from './Form'
import { Kv } from './Kv'
import { Mtx } from './Mtx'
import { Note } from './Note'
import { Sim } from './Sim'
import { Table } from './Table'
import { Tl } from './Tl'
import { Ver } from './Ver'

/**
 * Dispatch on the block kind.
 *
 * The switch is exhaustive over `BlockSpec['t']` and TypeScript enforces it —
 * adding a seventeenth block kind to the spec will not compile until it is
 * rendered here, which is the only guarantee that a screen cannot silently
 * lose a section. `decide` was the fifteenth (#111) and `form` the sixteenth.
 */

export interface BlockProps {
  readonly block: BlockSpec
  readonly query: string
  readonly onQuery: (value: string) => void
  readonly cursor: number
  readonly onRowClick: (index: number) => void
  readonly facetsOn: ReadonlySet<string>
  readonly onToggleFacet: (key: string) => void
  /** Standby cell, or a role this screen is not scoped to. */
  readonly writeBlocked: boolean
  /**
   * Why writing is or is not available, in words. Shown beside the commit
   * control on the two blocks that have one. A greyed button with no reason
   * beside it is the thing that generates the support ticket.
   */
  readonly writeNote: string
  readonly onNavigate: (screenId: string) => void
  readonly onFlash: (message: string) => void
}

export function Block(props: BlockProps) {
  const { block } = props

  // A note is the one block that never takes the heading strip: it carries its
  // own kicker, and a heading above it would say the same thing twice.
  if (block.t === 'note') {
    return (
      <div style={{ marginBottom: '16px' }}>
        <Note block={block} />
      </div>
    )
  }

  return (
    <BlockShell title={block.title} src={block.src} fresh={block.fresh} note={block.note}>
      <Body {...props} />
    </BlockShell>
  )
}

function Body({
  block,
  query,
  onQuery,
  cursor,
  onRowClick,
  facetsOn,
  onToggleFacet,
  writeBlocked,
  writeNote,
  onNavigate,
  onFlash,
}: BlockProps) {
  switch (block.t) {
    case 'table':
      return (
        <Table
          block={block}
          query={query}
          onQuery={onQuery}
          cursor={cursor}
          onRowClick={onRowClick}
          facetsOn={facetsOn}
          onToggleFacet={onToggleFacet}
        />
      )
    case 'cards':
      return <Cards block={block} onNavigate={onNavigate} />
    case 'bal':
      return <Bal block={block} />
    case 'kv':
      return <Kv block={block} />
    case 'cmp':
      return <Cmp block={block} />
    case 'mtx':
      return <Mtx block={block} onFlash={onFlash} />
    case 'ver':
      return <Ver block={block} writeBlocked={writeBlocked} writeNote={writeNote} />
    case 'apr':
      return <Apr block={block} writeBlocked={writeBlocked} />
    case 'composer':
      return <Composer block={block} writeBlocked={writeBlocked} writeNote={writeNote} />
    // The one block that submits. It takes the same two shell props as the
    // others and adds nothing to this dispatch: which permission gates it, and
    // what it posts to, are the adapter's to decide and travel on the block.
    case 'decide':
      return <Decide block={block} writeBlocked={writeBlocked} writeNote={writeNote} />
    // The general write: creates a record, or publishes a version over the one
    // that is live. Law 6's three obligations — the wording, the diff and the
    // required effective_from — are the component's rather than each adapter's.
    case 'form':
      return <Form block={block} writeBlocked={writeBlocked} writeNote={writeNote} />
    case 'tl':
      return <Tl block={block} />
    case 'chart':
      return <Chart block={block} />
    case 'sim':
      return <Sim block={block} />
    case 'actions':
      return <Actions block={block} writeBlocked={writeBlocked} />
    case 'note':
      return <Note block={block} />
  }
}
